/* Curated, optional companions. Ordinary Shopify variants, inventory and prices. */
(() => {
  'use strict';
  const STORAGE_KEY = 'otf_wear_together_pending_v1';
  const OPERATION = '_otf_wear_operation';
  const ROLE = '_otf_wear_role';
  const variantId = value => String(value?.variant_id ?? value?.id ?? value ?? '');
  const validId = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const integer = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
  const lines = cart => {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid bag response');
    return cart.items.filter(line => integer(line.quantity));
  };
  const owns = (line, record) => line.properties?.[OPERATION] === record.operation && record.expected.some(expected =>
    variantId(line) === expected.variant && line.properties?.[ROLE] === expected.role);
  const normalQuantity = (cart, id) => lines(cart).filter(line => variantId(line) === String(id) && !line.selling_plan_allocation)
    .reduce((total, line) => total + Number(line.quantity), 0);
  const publicProperties = properties => Object.entries(properties || {}).filter(([key]) => !key.startsWith('_'))
    .sort(([a], [b]) => a.localeCompare(b));
  const sameProperties = (a, b) => JSON.stringify(publicProperties(a)) === JSON.stringify(publicProperties(b));

  function validateRecord(record) {
    if (!record || record.version !== 1 || typeof record.operation !== 'string' || !/^[\w-]{12,100}$/.test(record.operation) ||
      typeof record.uncertain !== 'boolean' || !Array.isArray(record.expected) || !record.expected.length || record.expected.length > 12 ||
      !Array.isArray(record.goals) || record.goals.length > 12) throw new Error('The earlier bag update needs assistance');
    if (!record.expected.every(item => validId(item.variant) && integer(item.quantity) && /^primary$|^companion-\d+$/.test(item.role)) ||
      !record.goals.every(item => validId(item.variant) && integer(item.quantity))) throw new Error('The earlier bag update needs assistance');
    return record;
  }

  function sessionCheckpoint(storage) {
    return {
      read: () => {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return validateRecord(JSON.parse(raw));
      },
      write: record => {
        validateRecord(record);
        const raw = JSON.stringify(record);
        storage.setItem(STORAGE_KEY, raw);
        if (storage.getItem(STORAGE_KEY) !== raw) throw new Error('Bag recovery could not be saved');
      },
      clear: () => storage.removeItem(STORAGE_KEY),
    };
  }

  function quantityAllowed(variant, quantity) {
    const min = integer(variant.quantityMin ?? variant.quantity_rule?.min) || 1;
    const step = integer(variant.quantityIncrement ?? variant.quantity_rule?.increment) || 1;
    const rawMax = variant.quantityMax ?? variant.quantity_rule?.max;
    const max = rawMax == null ? null : Number(rawMax);
    if (max !== null && (!Number.isSafeInteger(max) || max < 0)) return false;
    return !!integer(quantity) && quantity >= min && (quantity - min) % step === 0 && (max === null || quantity <= max);
  }

  function collectionCompanions(primary, vendor) {
    const normalize = value => String(value || '').trim().toLowerCase();
    const collection = normalize(primary?.collectionName);
    const brand = normalize(vendor);
    if (!collection || !brand) return [];
    return (primary.companions || []).filter(variant => normalize(variant.vendor) === brand && normalize(variant.collectionName) === collection);
  }

  function groupCompanions(companions = []) {
    const groups = new Map();
    for (const variant of companions) {
      if (!validId(variant.id) || !validId(variant.productId)) continue;
      const key = String(variant.productId);
      if (!groups.has(key)) groups.set(key, { id: key, variants: [] });
      const group = groups.get(key);
      if (!group.variants.some(item => String(item.id) === String(variant.id))) group.variants.push(variant);
    }
    return [...groups.values()];
  }

  // Only an explicitly named One Size option is preselected; apparel always needs a deliberate choice.
  function fixedVariant(group) {
    if (group.variants.length !== 1) return null;
    const variant = group.variants[0];
    return (variant.options || []).some(value => /^(one[ -]?size(?: fits all)?|os(?:fa|fm)?)$/i.test(String(value).trim())) ? variant : null;
  }

  function planSelection(cart, selection, operation) {
    const existing = lines(cart);
    const seen = new Set();
    const items = [];
    let reusedQuantity = 0;
    for (const [index, selected] of selection.entries()) {
      const id = variantId(selected.variant);
      if (!validId(id) || seen.has(id) || !quantityAllowed(selected.variant, selected.quantity)) throw new Error('Choose an available size and quantity for every selected item.');
      seen.add(id);
      const existingQuantity = existing.filter(line => variantId(line) === id && !line.selling_plan_allocation && sameProperties(line.properties, selected.properties))
        .reduce((sum, line) => sum + Number(line.quantity), 0);
      const quantity = Math.max(0, selected.quantity - existingQuantity);
      reusedQuantity += Math.min(selected.quantity, existingQuantity);
      if (quantity) items.push({ id: Number(id), quantity, properties: {
        ...(selected.properties || {}), [OPERATION]: operation, [ROLE]: index === 0 ? 'primary' : `companion-${index}`,
      } });
    }
    return { items, reusedQuantity, addedQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
      goals: selection.map(item => ({ variant: variantId(item.variant), quantity: item.quantity })) };
  }

  function committed(cart, record) {
    const owned = lines(cart).filter(line => owns(line, record));
    return record.expected.every(expected => owned.filter(line => variantId(line) === expected.variant && line.properties[ROLE] === expected.role)
      .reduce((sum, line) => sum + Number(line.quantity), 0) === expected.quantity) &&
      record.goals.every(goal => normalQuantity(cart, goal.variant) >= goal.quantity) &&
      owned.every(line => record.expected.some(expected => expected.variant === variantId(line) && expected.role === line.properties[ROLE]));
  }

  function createPairFlow({ api, checkpoint, token = () => globalThis.crypto.randomUUID() }) {
    let pending = checkpoint.read();
    if (pending) validateRecord(pending);
    let busy = false;
    let releaseHold = pending ? api.holdCheckout() : null;
    const release = () => { releaseHold?.(); releaseHold = null; };
    const clear = () => { checkpoint.clear(); pending = null; release(); };
    const result = (status, message, values = {}) => ({ status, message, ...values });
    const review = () => result('review_required', 'Your bag update is still unconfirmed. Check it below before adding again or checking out.');
    const success = (cart, record) => {
      clear();
      const reused = Number(record.reusedQuantity) || 0;
      const added = Number(record.addedQuantity) || 0;
      return result(added ? 'added' : 'already_in_bag', added
        ? reused ? 'Selected items are in your bag. Items already there were kept; only the missing quantities were added.' : 'Your selected items were added to your bag.'
        : 'Your selected items are already in your bag. No extra quantities were added.', { cart, addedQuantity: added, reusedQuantity: reused });
    };
    async function rollback(record) {
      // Remove only this request's new lines, never a pre-existing line or a foreign related item.
      for (let attempt = 0; attempt < 25; attempt += 1) {
        const cart = await api.read();
        const current = lines(cart);
        const owned = current.filter(line => owns(line, record));
        if (!owned.length) return cart;
        const removable = owned.find(line => line.key && !current.some(child => child.parent_relationship?.parent_key === line.key));
        if (!removable) throw new Error('Related bag items need review');
        try { await api.change(removable.key, 0); }
        catch (error) {
          const checked = await api.read();
          if (lines(checked).some(line => line.key === removable.key && owns(line, record))) throw error;
        }
      }
      throw new Error('Bag recovery needs review');
    }
    async function settle() {
      const cart = await api.read();
      if (committed(cart, pending)) return success(cart, pending);
      if (pending.uncertain) return review();
      const checked = await rollback(pending);
      clear();
      return result('unavailable', 'The complete selection could not be added. Its new items were removed; your existing bag items were kept.', { cart: checked });
    }
    async function claim(selection) {
      if (busy || api.isBusy?.()) return result('busy', 'Please wait for your bag to finish updating.');
      if (pending) return review();
      if (!Array.isArray(selection) || selection.length < 2 || selection.length > 12) return result('selection_required', 'Choose a size for each selected item.');
      busy = true;
      let finish;
      let writeAttempted = false;
      try {
        finish = api.begin();
        releaseHold = api.holdCheckout();
        const before = await api.read();
        const operation = token();
        if (typeof operation !== 'string' || !/^[\w-]{12,100}$/.test(operation) || lines(before).some(line => line.properties?.[OPERATION] === operation)) throw new Error('Bag update could not be prepared.');
        const plan = planSelection(before, selection, operation);
        if (!plan.items.length) { release(); return success(before, plan); }
        const availability = await api.availability(selection.map(item => item.variant));
        for (const selected of selection) {
          const live = availability[variantId(selected.variant)];
          if (!live || !Number.isFinite(Number(live.price)) || Number(live.price) !== Number(selected.variant.price)) throw new Error('A selected price has changed. Refresh this page to review the current subtotal.');
        }
        for (const item of plan.items) {
          const selected = selection.find(value => variantId(value.variant) === String(item.id));
          const live = availability[String(item.id)];
          if (!live || live.available !== true) throw new Error('A selected item just sold out. Refresh to see the latest availability.');
          const rules = { ...selected.variant, ...(live.quantity_rule ? {
            quantityMin: live.quantity_rule.min, quantityMax: live.quantity_rule.max, quantityIncrement: live.quantity_rule.increment,
          } : {}) };
          const finalQuantity = normalQuantity(before, item.id) + item.quantity;
          if (!quantityAllowed(rules, finalQuantity)) throw new Error('A selected quantity is unavailable. Check the quantity and try again.');
          const tracked = rules.inventoryManagement || live.inventory_management;
          const policy = String(live.inventory_policy ?? rules.inventoryPolicy ?? '').toLowerCase();
          const stock = live.inventory_quantity ?? rules.inventoryQuantity;
          if (tracked && policy === 'deny' && stock != null && Number.isFinite(Number(stock)) && finalQuantity > Number(stock)) throw new Error('There is not enough stock for that quantity. Choose a smaller quantity.');
        }
        const record = { version: 1, operation, uncertain: true, expected: plan.items.map(item => ({
          variant: String(item.id), role: item.properties[ROLE], quantity: item.quantity,
        })), goals: plan.goals, addedQuantity: plan.addedQuantity, reusedQuantity: plan.reusedQuantity };
        checkpoint.write(record); // Persistence must succeed BEFORE the one and only add request.
        pending = record;
        writeAttempted = true;
        let settled = false;
        try {
          const response = await api.add(plan.items);
          settled = Array.isArray(response?.items) && record.expected.every(expected => response.items.some(line =>
            owns(line, record) && variantId(line) === expected.variant && line.properties[ROLE] === expected.role && Number(line.quantity) === expected.quantity));
        } catch (error) { settled = error?.cartWriteOutcome === 'rejected'; }
        if (settled) {
          pending = { ...record, uncertain: false };
          checkpoint.write(pending);
        }
        return await settle();
      } catch (error) {
        if (writeAttempted) return review();
        release();
        return result('unavailable', error?.message || 'This selection is unavailable right now. Your bag was not changed.');
      } finally { try { await finish?.(); } catch { /* A confirmed cart outcome must not become another add intent. */ } finally { busy = false; } }
    }
    async function recover() {
      if (busy || api.isBusy?.()) return result('busy', 'Please wait for your bag to finish updating, then check again.');
      if (!pending) return result('unchanged', 'Your bag is ready.');
      busy = true;
      let finish;
      try { finish = api.begin(); releaseHold ||= api.holdCheckout(); return await settle(); }
      catch { return review(); }
      finally { try { await finish?.(); } catch { /* Preserve the recovery result if native rendering fails. */ } finally { busy = false; } }
    }
    return { claim, recover, get busy() { return busy; }, get recoveryRequired() { return !!pending; } };
  }

  const MUTATION_TARGETS = 'product-form form,form[data-type="add-to-cart-form"],quantity-input,cart-remove-button,variant-radios,variant-selects,.otf-native-payment,[data-otf-card-sizes],[data-otf-size-add],.quick--add-form,.quick--add-to-cart,form#cart,form#CartPopup-Form,.additional-checkout-buttons,.shopify-payment-button,[name="checkout"],a[href^="/checkout"]';
  function blockCartMutations(doc) {
    const guard = event => {
      if (event.type === 'keydown' && ['Tab', 'Escape'].includes(event.key)) return;
      const target = event.target;
      // Both native and the older Peachtree recovery controls must remain usable.
      if (target.closest?.('[data-wear-together-recover],[data-wear-together-view-bag],[data-otf-offer-recover],[data-otf-cart-recovery],#cart-errors a[href],#CartPopup-CartErrors a[href]')) return;
      if (!target.closest?.(MUTATION_TARGETS) && !target.form?.matches?.(MUTATION_TARGETS)) return;
      if (event.cancelable) event.preventDefault();
      event.stopImmediatePropagation();
    };
    const events = ['pointerdown', 'click', 'keydown', 'beforeinput', 'input', 'change', 'submit'];
    events.forEach(type => doc.addEventListener(type, guard, true));
    // Accelerated checkouts may contain cross-origin frames, so capture listeners alone are insufficient.
    const paymentStates = new Map();
    const Observer = doc.defaultView?.MutationObserver;
    const process = records => records.forEach(record => {
      if (record.type !== 'attributes') return;
      const state = paymentStates.get(record.target);
      if (!state) return;
      if (state.ownWrites) state.ownWrites -= 1;
      else state.restore = record.target.getAttribute('inert');
    });
    const scan = () => doc.querySelectorAll('.otf-native-payment,.shopify-payment-button,.additional-checkout-buttons').forEach(node => {
      let state = paymentStates.get(node);
      if (!state) { state = { restore: node.getAttribute('inert'), ownWrites: 0 }; paymentStates.set(node, state); }
      if (!node.hasAttribute('inert')) { state.ownWrites += 1; node.setAttribute('inert', ''); }
    });
    const observer = Observer && doc.body ? new Observer(records => { process(records); scan(); }) : null;
    if (observer) { observer.observe(doc.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['inert'] }); scan(); }
    return () => {
      if (observer) { process(observer.takeRecords()); observer.disconnect(); }
      paymentStates.forEach((state, node) => {
        if (state.restore === null) node.removeAttribute('inert');
        else node.setAttribute('inert', state.restore);
      });
      events.forEach(type => doc.removeEventListener(type, guard, true));
    };
  }

  function createThemeAdapter(native, doc, root) {
    if (!['beginMutation', 'holdCheckout', 'fetchCartText', 'reconcileCart'].every(name => typeof native?.[name] === 'function')) throw new Error('Bag integration is loading. Please try again.');
    if (typeof root !== 'string' || !/^\/(?!\/)/.test(root)) throw new Error('Invalid storefront route');
    const localeRoot = root.endsWith('/') ? root : `${root}/`;
    const isBusy = () => !!(native.recoveryRelease || native.mutationsInFlight || native.pendingQuantityChanges ||
      globalThis.__otfPeachtreeOffer?.busy || globalThis.__otfPeachtreeOffer?.recoveryRequired);
    const request = async (url, body) => {
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      const options = body ? { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : { headers };
      const response = JSON.parse(await native.fetchCartText(url, options, new AbortController(), true));
      if (response.status >= 400 || response.errors) {
        const error = new Error(typeof response.description === 'string' ? response.description : typeof response.message === 'string' ? response.message : 'Bag update failed');
        if ([400, 422].includes(Number(response.status)) && (response.description || response.message || response.errors)) error.cartWriteOutcome = 'rejected';
        throw error;
      }
      return response;
    };
    return {
      isBusy,
      begin: () => { if (isBusy()) throw new Error('Your bag is updating. Please try again shortly.'); return native.beginMutation(); },
      holdCheckout: () => {
        const unblock = blockCartMutations(doc);
        let release;
        try { release = native.holdCheckout(); } catch (error) { unblock(); throw error; }
        return () => { try { release(); } finally { unblock(); } };
      },
      read: () => request(`${localeRoot}cart.js`),
      add: items => request(`${localeRoot}cart/add.js`, { items }),
      change: (key, quantity) => request(`${localeRoot}cart/change.js`, { id: key, quantity }),
      availability: async variants => {
        const handles = [...new Set(variants.map(variant => variant.handle))];
        if (handles.some(handle => typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(handle))) throw new Error('This pairing needs an availability update.');
        const products = await Promise.all(handles.map(handle => request(`${localeRoot}products/${encodeURIComponent(handle)}.js`)));
        const answer = {};
        products.forEach(product => (product.variants || []).forEach(variant => { answer[String(variant.id)] = variant; }));
        return answer;
      },
      refresh: () => native.reconcileCart(null, true),
    };
  }

  const exports = { STORAGE_KEY, OPERATION, ROLE, validateRecord, sessionCheckpoint, quantityAllowed, collectionCompanions, groupCompanions, fixedVariant, planSelection, committed, createPairFlow, createThemeAdapter, blockCartMutations };
  if (typeof module !== 'undefined' && module.exports) module.exports = exports;
  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;
  if (globalThis.__otfWearTogether) return;

  let flow;
  let adapter;
  let setupError = '';
  let lastResult;
  let recovering = false;
  let recoveryBar;
  const modules = new Set();
  const checkpoint = sessionCheckpoint(sessionStorage);
  const textNode = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = String(text);
    return node;
  };
  const rootPath = () => globalThis.Shopify?.routes?.root || '/';
  function recoveryView(message) {
    if (!recoveryBar) {
      recoveryBar = textNode('aside', 'otf-wear-recovery');
      recoveryBar.setAttribute('aria-label', 'Bag update review');
      const heading = textNode('strong', '', 'Check your bag update');
      const status = textNode('p', 'otf-wear-recovery__status');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      const actions = textNode('div', 'otf-wear-recovery__actions');
      const retry = textNode('button', '', 'Check bag update');
      retry.type = 'button';
      retry.setAttribute('data-wear-together-recover', '');
      retry.addEventListener('click', async () => {
        if (!flow || recovering) return;
        recovering = true;
        synchronize();
        try { lastResult = await flow.recover(); await refreshBag(lastResult, false); }
        finally { recovering = false; synchronize(); }
      });
      const link = textNode('a', '', 'View bag');
      link.href = `${rootPath()}cart`;
      link.setAttribute('data-wear-together-view-bag', '');
      actions.append(retry, link);
      recoveryBar.append(heading, status, actions);
      document.body.append(recoveryBar);
    }
    recoveryBar.querySelector('p').textContent = message;
    recoveryBar.querySelector('button').disabled = !flow || recovering || flow.busy;
    recoveryBar.hidden = false;
  }
  function synchronize() {
    const pending = flow?.recoveryRequired || setupError;
    if (pending) recoveryView(lastResult?.message || setupError || 'The earlier bag update needs a check before you add again or check out.');
    else if (recoveryBar) recoveryBar.hidden = true;
    modules.forEach(instance => instance.updateAction());
  }
  async function refreshBag(result, open) {
    if (!result?.cart || !adapter) return;
    try {
      if (await adapter.refresh() === false) throw new Error('Bag display could not be refreshed');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wear-together' });
      if (open && ['added', 'already_in_bag'].includes(result.status)) {
        const popup = document.querySelector('[data-popup-type="cart-popup"]');
        if (popup) {
          document.querySelectorAll('quick-add-modal').forEach(node => node.classList.add('hide-popup'));
          popup.classList.remove('hide-popup');
          document.body.setAttribute('active-popup', 'cart');
          document.querySelector('.theme-popup-overlay')?.classList.remove('hidden-overlay');
          setTimeout(() => document.querySelector('.cart--popup-recommandation')?.classList.add('recommandation-active'), 450);
        }
      }
    } catch {
      result.message += ' Your bag display needs a refresh; open your bag to review the confirmed items.';
    }
  }

  class WearTogether extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try { this.config = JSON.parse(this.querySelector('[data-wear-together-config]')?.textContent || '{}'); }
      catch { return; }
      this.form = document.getElementById(this.dataset.formId);
      this.rows = this.querySelector('[data-wear-together-rows]');
      this.status = this.querySelector('[data-wear-together-status]');
      if (!this.form || !this.rows || !Array.isArray(this.config.variants)) return;
      this.selections = new Map();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.onFormChange = event => {
        if (event.target.closest?.('otf-wear-together')) return;
        if (event.target.name === 'id' && event.target.form === this.form) this.primaryChanged = true;
        if (event.target.closest?.('variant-radios,variant-selects')) this.primaryChanged = true;
        if (event.target.form === this.form || this.form.contains(event.target)) this.scheduleUpdate();
      };
      document.addEventListener('change', this.onFormChange, { signal });
      document.addEventListener('input', this.onFormChange, { signal });
      this.form.addEventListener('submit', event => this.submit(event), { capture: true, signal });
      this.observer = new MutationObserver(() => this.scheduleUpdate());
      this.observer.observe(this.form, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'value', 'data-otf-unavailable'] });
      modules.add(this);
      this.render();
      synchronize();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); modules.delete(this); this.restoreAction(); this.ready = false;
    }
    scheduleUpdate() {
      if (this.scheduled) return;
      this.scheduled = true;
      queueMicrotask(() => { this.scheduled = false; if (this.isConnected) this.render(); });
    }
    primary() {
      const input = this.form.querySelector('[name="id"]');
      const selected = input?.value || (!this.primaryChanged ? this.config.selectedVariantId : null);
      return this.config.variants.find(variant => String(variant.id) === String(selected));
    }
    quantity() { return integer(new FormData(this.form).get('quantity') || 1); }
    primaryReady() {
      const input = this.form.querySelector('[name="id"]');
      return !!input && !input.disabled && !!input.value && this.primary()?.available === true;
    }
    selectedRows() { return [...this.selections.values()].filter(item => item.checked); }
    selectedVariant(item) { return item.group.variants.find(variant => String(variant.id) === String(item.variantId)); }
    money(cents) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.config.currency || 'USD' }).format(Number(cents) / 100); }
      catch { return `${(Number(cents) / 100).toFixed(2)} ${this.config.currency || 'USD'}`; }
    }
    say(message) { if (this.status) this.status.textContent = message; }
    render() {
      const primary = this.primary();
      const groups = groupCompanions(collectionCompanions(primary, this.config.vendor)).filter(group => group.variants.some(variant => variant.available));
      const signature = JSON.stringify([primary?.id, groups.map(group => group.variants.map(variant => [variant.id, variant.available]))]);
      if (signature === this.signature) { this.updateAction(); return; }
      const previous = this.selections;
      this.signature = signature;
      this.selections = new Map();
      let cleared = false;
      groups.forEach(group => {
        const old = previous.get(group.id);
        const stillAllowed = old && group.variants.some(variant => String(variant.id) === old.variantId && variant.available);
        const fixed = fixedVariant(group);
        this.selections.set(group.id, { group, checked: !!old?.checked && !!stillAllowed,
          variantId: stillAllowed ? old.variantId : fixed?.available ? String(fixed.id) : '' });
        if (old?.checked && !stillAllowed) cleared = true;
      });
      for (const [key, old] of previous) if (old.checked && !this.selections.has(key)) cleared = true;
      this.rows.replaceChildren();
      this.hidden = !groups.length;
      if (!groups.length) { this.restoreAction(); return; }
      const heading = textNode('h3', 'otf-wear__heading', 'Wear it together');
      const caption = textNode('p', 'otf-wear__caption', `From ${primary.collectionName} by ${this.config.vendor}. Choose the pieces you want.`);
      this.rows.append(heading, caption);
      this.selections.forEach(item => this.renderRow(item));
      const total = textNode('p', 'otf-wear__total'); total.setAttribute('data-wear-together-total', '');
      const note = textNode('p', 'otf-wear__note', 'Subtotal before offers. Any available offers appear in your bag.');
      this.rows.append(total, note);
      if (cleared) this.say('Your selection changed. Choose the matching item again.');
      this.updateAction();
    }
    safeUrl(value, image = false) {
      try {
        const url = new URL(value, location.origin);
        if (image ? url.protocol !== 'https:' : url.origin !== location.origin) return null;
        return url.href;
      } catch { return null; }
    }
    renderRow(item) {
      const first = item.group.variants[0];
      const row = textNode('div', 'otf-wear__row');
      const label = textNode('label', 'otf-wear__choice');
      const checkbox = document.createElement('input'); checkbox.type = 'checkbox'; checkbox.checked = item.checked;
      checkbox.disabled = !item.group.variants.some(variant => variant.available);
      const accessible = textNode('span', 'otf-wear__checkbox-label', `Include ${first.productTitle}`);
      const imageWell = textNode('span', 'otf-wear__image');
      const imageUrl = this.safeUrl(first.image, true);
      if (imageUrl) { const image = document.createElement('img'); image.src = imageUrl; image.alt = ''; image.loading = 'lazy'; image.width = 144; image.height = 180; imageWell.append(image); item.imageNode = image; }
      label.append(checkbox, accessible, imageWell);
      const details = textNode('div', 'otf-wear__details');
      const url = this.safeUrl(first.url);
      const title = textNode(url ? 'a' : 'span', 'otf-wear__title', first.productTitle);
      if (url) title.href = url;
      item.titleNode = title;
      details.append(title);
      const selected = this.selectedVariant(item) || first;
      const colorOptions = (first.options || []).filter((value, index) => !/size/i.test(first.optionNames?.[index] || ''));
      item.colorNode = textNode('p', 'otf-wear__variant', colorOptions.join(' / ') || first.title);
      details.append(item.colorNode);
      if (fixedVariant(item.group)) {
        details.append(textNode('p', 'otf-wear__variant', 'One size'));
      } else {
        const selectLabel = textNode('label', 'otf-wear__size');
        const hasSize = (first.optionNames || []).some(name => /size/i.test(name));
        const sameColour = new Set(item.group.variants.map(variant => (variant.options || []).filter((value, index) => !/size/i.test(variant.optionNames?.[index] || '')).join(' / '))).size === 1;
        const optionLabel = !sameColour ? (hasSize ? 'Color / size' : 'Color / option') : hasSize ? 'Size' : 'Option';
        selectLabel.append(textNode('span', '', optionLabel));
        const select = document.createElement('select');
        select.setAttribute('aria-label', `Choose ${optionLabel.toLowerCase()} for ${first.productTitle}`);
        const placeholder = textNode('option', '', `Choose ${!sameColour ? 'color and ' : ''}${hasSize ? 'a size' : 'an option'}`); placeholder.value = ''; select.append(placeholder);
        item.group.variants.forEach(variant => {
          const sizeOptions = (variant.options || []).filter((value, index) => /size/i.test(variant.optionNames?.[index] || ''));
          const option = textNode('option', '', `${sameColour && sizeOptions.length ? sizeOptions.join(' / ') : variant.title}${variant.available ? '' : ' — Sold out'}`);
          option.value = String(variant.id); option.disabled = !variant.available; select.append(option);
        });
        select.value = item.variantId;
        select.addEventListener('change', () => { item.variantId = select.value; select.removeAttribute('aria-invalid'); this.updateAction(); });
        selectLabel.append(select); details.append(selectLabel); item.select = select;
      }
      const price = textNode('span', 'otf-wear__price', this.money(selected.price));
      price.setAttribute('data-wear-together-price', ''); item.priceNode = price;
      if (checkbox.disabled) details.append(textNode('span', 'otf-wear__variant', 'Sold out'));
      checkbox.addEventListener('change', () => { item.checked = checkbox.checked; this.updateAction(); });
      row.append(label, details, price); this.rows.append(row);
    }
    action() { return this.form.querySelector('.product-form__submit') || this.form.querySelector('[type="submit"][name="add"]'); }
    restoreAction() {
      if (this.buttonLabel && this.originalLabel != null && this.buttonLabel.textContent === this.appliedLabel) this.buttonLabel.textContent = this.originalLabel;
      this.form?.querySelectorAll('.otf-native-payment[data-otf-wear-hidden]').forEach(node => { node.hidden = node.dataset.otfWearPreviousHidden === 'true'; delete node.dataset.otfWearHidden; delete node.dataset.otfWearPreviousHidden; });
      this.appliedLabel = null;
    }
    updateAction() {
      if (!this.form) return;
      const button = this.action();
      const label = button?.querySelector(':scope > span:not(.loading-overlay__spinner)');
      if (!button || !label) return;
      if (label !== this.buttonLabel) { this.buttonLabel = label; this.originalLabel = label.textContent; this.appliedLabel = null; }
      else if (this.appliedLabel == null || label.textContent !== this.appliedLabel) this.originalLabel = label.textContent;
      const selected = this.selectedRows();
      const primary = this.primary();
      const totalNode = this.querySelector('[data-wear-together-total]');
      this.selections?.forEach(item => {
        const variant = this.selectedVariant(item);
        const price = variant && this.money(variant.price);
        if (price && item.priceNode && item.priceNode.textContent !== price) item.priceNode.textContent = price;
        if (!variant) return;
        const color = (variant.options || []).filter((value, index) => !/size/i.test(variant.optionNames?.[index] || '')).join(' / ') || variant.title;
        if (item.colorNode && item.colorNode.textContent !== color) item.colorNode.textContent = color;
        const url = this.safeUrl(variant.url);
        if (url && item.titleNode && item.titleNode.href !== url) item.titleNode.href = url;
        const image = this.safeUrl(variant.image, true);
        if (image && item.imageNode && item.imageNode.src !== image) item.imageNode.src = image;
      });
      const complete = selected.every(item => this.selectedVariant(item)?.available);
      const quantity = this.quantity() || 1;
      const total = primary ? quantity * (Number(primary.price) + selected.reduce((sum, item) => sum + Number(this.selectedVariant(item)?.price || 0), 0)) : 0;
      if (totalNode) { totalNode.hidden = !selected.length || !complete; const totalText = `${quantity > 1 ? `${quantity} of each · ` : ''}Selected subtotal ${this.money(total)}`; if (totalNode.textContent !== totalText) totalNode.textContent = totalText; }
      if (!selected.length) { this.restoreAction(); return; }
      this.form.querySelectorAll('.otf-native-payment').forEach(node => {
        if (!node.hasAttribute('data-otf-wear-hidden')) { node.dataset.otfWearPreviousHidden = String(node.hidden); node.setAttribute('data-otf-wear-hidden', ''); }
        node.hidden = true;
      });
      // Preserve the theme's deliberate primary-size gate and its sold-out label.
      if (!this.primaryReady() || button.disabled) { this.restoreLabelOnly(); return; }
      this.appliedLabel = flow?.busy ? 'Updating bag…' : flow?.recoveryRequired ? 'Check bag update below' :
        !complete ? 'Choose matching size / option' : `${selected.length === 1 ? 'Add both' : 'Add selected'} · ${this.money(total)}`;
      if (label.textContent !== this.appliedLabel) label.textContent = this.appliedLabel;
    }
    restoreLabelOnly() { if (this.appliedLabel != null && this.buttonLabel?.textContent === this.appliedLabel) this.buttonLabel.textContent = this.originalLabel; this.appliedLabel = null; }
    async submit(event) {
      const selected = this.selectedRows();
      if (!selected.length) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (!this.primaryReady() || this.action()?.disabled || this.action()?.getAttribute('aria-disabled') === 'true') { this.say('Choose your size first.'); return; }
      const missing = selected.find(item => !this.selectedVariant(item)?.available);
      if (missing) { this.say('Choose a size or option for each matching item.'); missing.select?.setAttribute('aria-invalid', 'true'); missing.select?.focus(); return; }
      if (!flow) { this.say('Bag integration is loading. Please try again shortly.'); return; }
      const formData = new FormData(this.form);
      if (formData.get('selling_plan')) { this.say('This selection needs to be added individually. Deselect the matching items to continue.'); return; }
      const properties = {};
      for (const [name, value] of formData.entries()) {
        const match = /^properties\[(.+)\]$/.exec(name);
        if (!match || !value) continue;
        if (typeof value !== 'string') { this.say('Add personalized items individually. Deselect the matching items to continue.'); return; }
        properties[match[1]] = value;
      }
      const primary = { ...this.primary(), handle: this.config.handle };
      const quantity = this.quantity();
      if (!quantityAllowed(primary, quantity)) { this.say('Choose an available quantity first.'); return; }
      const selection = [{ variant: primary, quantity, properties }, ...selected.map(item => ({ variant: this.selectedVariant(item), quantity }))];
      const promise = flow.claim(selection);
      this.say('Checking availability and updating your bag…'); synchronize();
      try { lastResult = await promise; await refreshBag(lastResult, true); this.say(lastResult.message); }
      catch { this.say('Your bag needs a check before trying again.'); }
      finally { synchronize(); }
    }
  }
  if (!customElements.get('otf-wear-together')) customElements.define('otf-wear-together', WearTogether);
  globalThis.__otfWearTogether = { get busy() { return !!flow?.busy; }, get recoveryRequired() { return !!flow?.recoveryRequired || !!setupError; } };

  async function bootstrap() {
    let saved = false;
    try { saved = !!sessionStorage.getItem(STORAGE_KEY); } catch { /* No persistent writes are allowed without recovery storage. */ }
    const unblockEarly = saved ? blockCartMutations(document) : null;
    if (saved) recoveryView('Checking the earlier bag update…');
    for (let attempt = 0; attempt < 100; attempt += 1) {
      const native = typeof CartItems !== 'undefined' ? CartItems : null;
      if (native?.beginMutation) {
        try {
          adapter = createThemeAdapter(native, document, rootPath());
          flow = createPairFlow({ api: adapter, checkpoint });
          unblockEarly?.();
          synchronize();
          if (flow.recoveryRequired) { lastResult = await flow.recover(); await refreshBag(lastResult, false); synchronize(); }
        } catch (error) {
          // A corrupt recovery record still needs the native checkout hold, including frame-based payment buttons.
          if (saved) { try { native.holdCheckout(); } catch { /* The early frame/event protection remains active. */ } }
          setupError = saved ? 'Your earlier bag update needs review. Reload this page to check it before checkout. Contact the store if it remains unconfirmed.' : '';
          synchronize();
        }
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    if (saved) { setupError = 'Your earlier bag update needs review. Reload this page to check it before checkout.'; synchronize(); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  else bootstrap();
})();
